import { Injectable, Injector } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { WidgetsAbstract } from 'src/app/abstract/widgets/widgets.abstract';
import { Logger } from 'src/app/core/logger';
import { WidgetsFieldModel } from 'src/app/models/widgets/widgets-field.model';
import { WidgetsCheckboxModel } from 'src/app/models/widgets/widgets-checkbox.model';
import { CloneService } from '../clone/clone.service';
import { SharedConstants } from 'src/app/shared/constants/shared-constant';
import { AbstractRender } from 'src/app/abstract/render.abstract';
import { Config } from 'src/app/config';
import { getKeyFromText } from '../shared/camelCase';
import { CommentService } from 'src/app/services/comment.service';
import { RenderService } from './render.service';

@Injectable({
  providedIn: 'root',
})
export class RenderCheckboxService extends AbstractRender {
  constructor(private injector: Injector, private logger: Logger, 
    private commentService: CommentService,
    private globalRender: RenderService) {
    super();
  }

  /**
   * Recursive render method
   *
   * @param widget A layout node
   * @param config Formly field config array
   * @param widgetRoot Root of the layout
   * @param formlyRoot Root of the FormlyFieldConfig
   * @param isInsideQuestion True if we are rendering a subtree inside a question
   * @param isRecursive True if we want to traverse the tree to the bottom. False if we just want to render a node.
   */
  render(
    widget: WidgetsAbstract,
    config: FormlyFieldConfig[],
    widgetRoot: WidgetsAbstract,
    formlyRoot: FormlyFieldConfig,
    isInsideQuestion: boolean,
    isRecursive: boolean
  ) {
    try {
      if (widget instanceof WidgetsCheckboxModel) {
        this.renderPrimitive(widget as WidgetsCheckboxModel, config, widgetRoot, formlyRoot);
      }
    } catch (error) {
      //currentWidget = widget as WidgetsRadioModel;
      //this.renderField(widget as WidgetsRadioModel, config, formlyRoot);
      this.logger.warn('render checkbox ' + error);
    }
  }

  /**
   * Renders a primitive node to a formly config object
   *
   * @param model The field or checkbox model to render
   * @param config The Formly Config object to render to
   * @param widgetRoot The root of the widget tree
   * @param formlyRoot The root of the formly object
   * @returns
   */
  private renderPrimitive(
    model: WidgetsCheckboxModel | WidgetsFieldModel,
    config: FormlyFieldConfig[],
    widgetRoot: WidgetsAbstract,
    formlyRoot: FormlyFieldConfig
  ) {
    const checkbox: WidgetsCheckboxModel = model as WidgetsCheckboxModel;

    // A checkbox primitive has a label (optional), a multicheckbox and an other input (optional)
    let fieldGroupPrimitive: FormlyFieldConfig = {};
    let fieldCheckbox: FormlyFieldConfig = {
      key: model.key,
    };    
    const fieldLabel: FormlyFieldConfig = {};
    let fieldOtherInput: FormlyFieldConfig = {};
    let fieldCommentInput: FormlyFieldConfig = {};
    let fieldExtendedCheckbox : FormlyFieldConfig = {};

    // Mix incoming template with the group
    if (checkbox.template) {
      const groupTemplate = JSON.parse(checkbox.template);
      // TODO: Check if this is really necessary
      // if (groupTemplate.expressions && groupTemplate.expressions.hide) {
      //   let grTemplate = {
      //     "expressions" : { "hide": groupTemplate.expressions.hide}
      //   }
      //   fieldGroupPrimitive = Object.assign({}, fieldGroupPrimitive, grTemplate);
      // }
      fieldGroupPrimitive = Object.assign({}, fieldGroupPrimitive, groupTemplate);
      // fieldCheckbox = Object.assign({}, fieldCheckbox, modelTemplate);
    }

    if (checkbox.fieldTemplate) {
      const fieldTemplate = JSON.parse(checkbox.fieldTemplate);
      fieldCheckbox = Object.assign({}, fieldCheckbox, fieldTemplate);
    }

    // 1. Label Field

    // Override labels on Fields
    // Go up to custom model and overrides labels
    const labelOverrides: Map<string, string> = new Map<string, string>();
    CloneService.getLabelOverrides(model, widgetRoot, labelOverrides);
    const overridedString = CloneService.overrideText(
      model.label,
      labelOverrides
    );

    if (model.label) {
      fieldLabel.template = overridedString;
    }

    // 2. Multicheckbox Field
    fieldCheckbox.type = "uw-multicheckbox";
    fieldCheckbox.props = {};    
    let defaultValue = {};
    
    // TODO: Formly V6: NgxFormly: legacy select option '{key, value}' is deprecated since v5.5, use '{value, label}' instead.
    // If options are provided, use them. If not, use sample data
    // model instanceof WidgetsCheckboxModel
    if (checkbox.options && checkbox.options.length > 0) {
      const option = checkbox.options.map(item => {
        // In layout we use for options
        // key - This is the label. Sometimes no value is provided.
        // value - This is the value sent. If no value provided, extract from the label.
        // If we only have key, then the value will be deducted: capitalized, only alphanumeric
        defaultValue = {...defaultValue, [item.value]: false };
        const container = {
          // key: item.key,
          // value: item.value ? item.value : getKeyFromText(item.key),
          label: item.label ? item.label : item.key,
          value: item.value ? item.value : getKeyFromText(item.key),
        };
        return container;
      });      

      if (checkbox.otherFlag) {
        const tempOtherValue = this.getOtherValue(model, checkbox);
        defaultValue = {...defaultValue, [tempOtherValue]: false }
        option.push({
          label: checkbox.otherLabel ? checkbox.otherLabel : Config.CONSTANTS.otherKeySuffix,
          value: tempOtherValue
        });
      }
      fieldCheckbox.props.options = option;
    }

    //Assign DefaultValue for all checkbox as false
    fieldCheckbox.defaultValue = defaultValue;

    if (model.description) {
      fieldCheckbox.props.description = model.description;
    }

    if (model.required) {
      fieldCheckbox.props.required = model.required;
      // If the field is required, at least one option has to be selected
      fieldCheckbox.validators = { validation: ['oneOptionSelected'] };
    }

    if (checkbox.commentDialogOn) {
      // if hold checked question
      const checkedQuestionHolder = new Map();
      let clickCount = 0;

      /**
       * @$event when checkbox checked it return true else false
       * **/
      fieldCheckbox.props.change = (field, $event) => {
        // if checkbox is checked then push a value in checkedArr & on unchecked remove a value from checkedArr
        if ($event.checked) {
          clickCount++;
          checkedQuestionHolder.set(clickCount, checkbox.parentId);
        } else {
          checkedQuestionHolder.delete(clickCount);
          clickCount--;
        }
        if (checkedQuestionHolder.size > 0) {
          this.commentService.sendValue(true, '' + checkbox.parentId)
        } else {
          this.commentService.sendValue(false, "" + checkbox.parentId)
        }
      }
    }

    // 3. "Other" Input Field
   
    if (checkbox.otherFlag) {
      const tempOtherValue = this.getOtherValue(model, checkbox);
      fieldOtherInput = {
        key: checkbox.otherKey ? checkbox.otherKey : model.key + Config.CONSTANTS.otherDescription,
        type: 'uw-input',
        validators: { validation: ['trimValidation'] },
        // expressions: { hide: '((model && model.' + model.key + ' && model.' + model.key + '.Other == true) ? false : true)'},
        expressions: { hide: '((model && model.' + model.key + ' && model.' + model.key + '.' + tempOtherValue +' == true) ? false : true)'},
        props: {
          // We dont want input placeholder since we have the other checkbox label
          //label: checkbox.otherLabel,
          description: checkbox.otherDescription ? checkbox.otherDescription : undefined,
          maxLength:  checkbox.otherMaxLength ? checkbox.otherMaxLength : SharedConstants.defaultOtherLabelLength,
          required: true
        }       
      };
    }
   
    // 4. Comment Field 
    if (checkbox.commentKey) {
      fieldCheckbox.props.commentKey = checkbox.commentKey;
    }

    // Mandatory on specific option selected    
    if (checkbox.commentFieldOn) {
      const temp_array = checkbox.commentFieldOn.split(';');
      let temp = '( ';
      for (let i = 0; i < temp_array.length; i++) {
        temp += '(model.' + model.key + ' && model.' + model.key + '.' + temp_array[i] + ' !== undefined && model.' + model.key + '.' + temp_array[i] + ' === true) ';
        if (i != temp_array.length - 1) {
          temp += ' || '
        }        
      }
      temp += ' ) ';
      // Comment Field Mandatory off- when specific option selected
      //This has more priority than commentfieldOn since we use &&  
      if(checkbox.commentFieldOff){
        const temp_array = checkbox.commentFieldOff.split(';');        
        for (let i = 0; i < temp_array.length; i++) {
          temp += '&& !(model.' + model.key + ' && model.' + model.key + '.' + temp_array[i] + ' !== undefined && model.' + model.key + '.' + temp_array[i] + ' === true) ';
          if (i != temp_array.length - 1) {
            temp += ' '
          }
        }
      }      
      fieldCommentInput = {
        key: checkbox.key + Config.CONSTANTS.commentKeySuffix,
        type: 'uw-input',
        className: 'prop-comment-checkbox',
        validators: { validation: ['trimValidation'] },
        expressions: { 
          hide:  '(' + temp + ' ? false : true)'
        },
        props: {
          label: 'Comment',
          maxLength: Config.CONSTANTS.commentMaxLength,
          required: (checkbox.commentFieldOnOptional !== undefined &&
            checkbox.commentFieldOnOptional === true) ? false : true,
        }
      };
    }

    // 5. Options disabled on
    if (checkbox.optionsDisabledOn) {
      const temp_array = checkbox.optionsDisabledOn.split(';');
      let temp = '';
      for (let i = 0; i < temp_array.length; i++) {
        temp += '(model.' + model.key + ' && model.' + model.key + '.' + temp_array[i] + ' !== undefined && model.' + model.key + '.' + temp_array[i] + ' === true) ';
        if (i != temp_array.length - 1) {
          temp += ' || '
        }
      }
      // : "model.disableOptionsOn === true"
      const optionsDisabledOn = {
          'props.options[1].disabled':  'true'
      };

      if (fieldCheckbox.expressions) {
        fieldCheckbox.expressions = { ...fieldCheckbox.expressions, ...optionsDisabledOn };
      } else {
        fieldCheckbox.expressions = optionsDisabledOn;
      }
    } 
    
    //Is Extended then add extra Key
    if(checkbox.isExtended){
      fieldExtendedCheckbox = {
        key: "IsExtended",
        defaultValue: true
      };
    }

    // 4. Prepare groups
    fieldGroupPrimitive.fieldGroupClassName = 'primitive-multicheck';
    fieldGroupPrimitive.fieldGroup = [];

    // 7. Render
    fieldLabel.className = 'prop-label';
    fieldGroupPrimitive.fieldGroup?.push(fieldLabel);
    fieldCheckbox.className = 'prop-multicheck';
    fieldGroupPrimitive.fieldGroup?.push(fieldCheckbox);
    if(checkbox.isExtended){
      fieldGroupPrimitive.fieldGroup?.push(fieldExtendedCheckbox);
    }
    if (checkbox.otherFlag) {
      fieldOtherInput.className = 'prop-comment-checkbox';
      fieldGroupPrimitive.fieldGroup?.push(fieldOtherInput);
    }
    if (checkbox.commentFieldOn) {
      fieldGroupPrimitive.fieldGroup?.push(fieldCommentInput);
      fieldGroupPrimitive.fieldGroup?.push(this.renderFieldTitle(checkbox));
      fieldGroupPrimitive.fieldGroup?.push(this.renderFieldCommentId(checkbox));
    }

    // Wrapper For Formly Group
    fieldGroupPrimitive.wrappers = ['field'];

    config.push(fieldGroupPrimitive);
  }

  getOtherValue(model: WidgetsCheckboxModel | WidgetsFieldModel, checkbox: WidgetsCheckboxModel): string {
    if (checkbox.isExtended) {
      return model.key + Config.CONSTANTS.otherKeySuffix;
    } else {
      return Config.CONSTANTS.otherDescription;
    }
  }

  renderFieldTitle(radio: any) {
    // 2. Question Title
    // "ParkingLotCoveredIndicator_FBQTI": "Question title",
    let radioCommentKey: FormlyFieldConfig;
    if (radio.label) {      
      radioCommentKey = {
        key: radio.key + Config.CONSTANTS.commentKeyTitle,
        defaultValue: radio.label       
      }
    } else {
      console.log();
      radioCommentKey = {
        key: radio.key + Config.CONSTANTS.commentKeyTitle,
        expressions: {
          defaultValue: `field.formControl?.setValue(field.parent?.parent?.props?.label)`
        }       
      }      
    }
    return radioCommentKey;
  }

  renderFieldCommentId(radio: any) {
    // 3. Comment id
    // "ParkingLotCoveredIndicator_FBCID": "c109_comment",
    let radioCommentKey: FormlyFieldConfig;

    radioCommentKey = {
      key: radio.key + Config.CONSTANTS.commentKeyId,
      defaultValue: 'c'+ this.globalRender.getCounter() + Config.CONSTANTS.commentSuffix      
    }
    return radioCommentKey;
  }
}
